import React, { useRef, useState } from "react";
import "./styles.css";
import { Box } from "ui-components";
import { connect } from "react-redux";
import {
  makeSelectAcraFileUploaded,
  makeSelectCompanyDetails,
} from "ducks/selectors/planService";
import {
  makeSelectMyInfo,
  makeSelectMyInfoDetails,
  makeSelectMyInfoIsInvalid,
  makeSelectMyInfoIsUsed,
} from "ducks/selectors/myInfo";
import {
  makeSelectAppSettings,
  makeSelectOrder,
  makeSelectOrderSummaryPayload,
} from "ducks/selectors/index";
import { createStructuredSelector } from "../../../../node_modules/reselect/es/index";
import { setData } from "ducks/actions";
import get from "lodash/get";
import {
  buildStoredJumioAuthHeaders,
  acraUpload,
} from "utils/helpers";
import { useDispatch } from "react-redux";
import { getRequestId } from "utils/localStorage";
import { createEnterpriseWorkFlow } from "ducks/actions/index";
import ErrorModal from "components/EnterpriseAutomated/ErrorModal/index";
import FormattedMessage from "components/FormattedMessage/index";


const AcraUpload = ({
  loading,
  onDrop,
  completed,
  label,
  testid,
  maxSize = 8 * 1024 * 1024,
  isAcraFileUpload,
  setIsAcraFileUpload,
  uploadAcraFile,
  order,
  setIsDeclaredCompanyDetails,
  companyDetails,
  resetIsAcraFileUpload,
  createEnterpriseWorkFlow: callCreateEnterpriseWorkFlow,
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState(
    get(companyDetails, "file_name", "")
  );
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  // Error modal state
  const [errorModal, setErrorModal] = useState({
    show: false,
    header: "",
    message: "",
  });

  const uploadIconUrl =
    "https://circles-marketing.s3.ap-southeast-1.amazonaws.com/ENT+webfront/upload+icon.png";

  const completedIconUrl =
    "https://circles-marketing.s3.ap-southeast-1.amazonaws.com/ENT+webfront/tick+system+icon.png";

  const uploadingIconUrl = "/assets/images/loading.gif";
  const showErrorModal = (header, message) => {
    setErrorModal({
      show: true,
      header: header,
      message: message,
    });
  };
  const closeErrorModal = () => {
    setErrorModal({
      show: false,
      header: "",
      message: "",
    });
  };
  /**
   * Upload API with 15 second timeout
   *
   * If the API does not respond within 15 seconds,
   * the promise will reject with NETWORK_TIMEOUT.
   */
  const uploadWithTimeout = (promise, timeout = 15000) => {
    return Promise.race([
      promise,

      new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error("NETWORK_TIMEOUT"));
        }, timeout);
      }),
    ]);
  };


  const handleFileUpload = async (file) => {
    const MAX_SIZE_MB = 8;

    // IMPORTANT:
    // Check file before accessing file.size
    if (!file) {
      return;
    }

    /**
     * Close any previous error modal before starting
     * a new upload attempt.
     */
    closeErrorModal();

    /**
     * Reset previous upload state
     */
    setIsAcraFileUpload(false);
    resetIsAcraFileUpload();
    setIsDeclaredCompanyDetails(false);
    setError("");
    setUploadComplete(false);

    /**
     * Validate file extension
     */
    if (!file.name.toLowerCase().endsWith(".oa")) {
      setError("Please upload a .oa file.");

      setIsAcraFileUpload(false);

      return;
    }


    /**
     * Validate file size
     */
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      showErrorModal(
        "FILE_SIZE_TOO_BIG",
        "The file size must be less than 8MB."
      );
      setIsAcraFileUpload(false);
      return;
    }


    if (file.size > maxSize) {
      setError("File size must be less than 8MB.");
      setIsAcraFileUpload(false);
      return;
    }


    /**
     * Set upload state
     */
    setFileName(file.name);
    setUploading(true);
    setUploadComplete(false);
    if (onDrop) {
      onDrop(file);
    }


    try {
      const headersValue = buildStoredJumioAuthHeaders();

      const requestId = getRequestId();

      const formData = new FormData();

      formData.append("file", file);


      console.log("ACRA UPLOAD REQUEST:", {
        requestId,
        fileName: file.name,
      });


      /**
       * API call with 15 second timeout
       */
      const result = await uploadWithTimeout(
        acraUpload(
          headersValue,
          formData,
          requestId
        ),
        15000
      );


      setUploading(false);


      console.log("ACRA UPLOAD RESPONSE:", result);


      /**
       * SUCCESS
       */
      if (result?.success) {
        dispatch(
          setData({
            key: "companyDetails",

            data: {
              company_name: result?.data?.company_name,

              uen: result?.data?.uen,

              company_address: result?.data?.company_address,

              file_name: file?.name,

              status: "SUCCESS",

              error: {
                header: "",
                message: "",
              },
            },
          })
        );


        /**
         * Notify Analytics / Workflow
         */
        callCreateEnterpriseWorkFlow(
          headersValue,

          {
            workflow: "upload_oa",

            request_id: requestId,

            status: "success",
          },

          () => {}
        );


        setUploadComplete(true);

        setIsAcraFileUpload(true);

        return;
      }


      /**
       * API returned an error
       */
      const errHeader = result?.error?.header;

      const errMessage = result?.error?.message;


      console.log("ACRA UPLOAD FAILED:", {
        header: errHeader,
        message: errMessage,
      });


      /**
       * Save error to Redux
       */
      dispatch(
        setData({
          key: "companyDetails",

          data: {
            company_name: "",

            uen: "",

            company_address: "",

            file_name: file?.name,

            status: "FAILURE",

            error: {
              header: errHeader,

              message: errMessage,
            },
          },
        })
      );


      /**
       * Notify Analytics / Workflow
       */
      callCreateEnterpriseWorkFlow(
        headersValue,

        {
          workflow: "upload_oa",

          request_id: requestId,

          status: "failed",
        },

        () => {}
      );


      setIsAcraFileUpload(false);


      /**
       * SHOW MODAL IMMEDIATELY
       *
       * No need to wait for Redux props to update.
       */
      showErrorModal(
        errHeader || "NETWORK_ERROR",
        errMessage || "Something went wrong. Please try again."
      );

    } catch (err) {
      console.error("ACRA upload exception:", err);


      setUploading(false);

      setIsAcraFileUpload(false);


      /**
       * API did not respond within 15 seconds
       */
      if (err?.message === "NETWORK_TIMEOUT") {
        console.log(
          "ACRA API did not respond within 15 seconds"
        );


        showErrorModal(
          "NETWORK_ERROR",
          "We couldn't connect to the server. Please check your network connection and try again."
        );

        return;
      }


      /**
       * Other network / unexpected errors
       */
      showErrorModal(
        "NETWORK_ERROR",
        "Something went wrong. Please try again."
      );
    }
  };


  const handleInputChange = (event) => {
    const file =
      event.target.files &&
      event.target.files[0];

    handleFileUpload(file);

    // Allows selecting the same file again
    event.target.value = "";
  };


  const handleDrop = (event) => {
    event.preventDefault();

    setDragging(false);

    if (loading || uploading) {
      return;
    }

    const file =
      event.dataTransfer.files &&
      event.dataTransfer.files[0];

    handleFileUpload(file);
  };


  const handleDragOver = (event) => {
    event.preventDefault();

    if (!loading && !uploading) {
      setDragging(true);
    }
  };


  const handleDragLeave = (event) => {
    event.preventDefault();

    setDragging(false);
  };


  const handleClick = () => {
    if (
      !loading &&
      !uploading &&
      fileInputRef.current
    ) {
      fileInputRef.current.click();
    }
  };


  const isLoading =
    loading || uploading;

  const isCompleted =
    completed || uploadComplete;


  return (
    <div className="company-info-container">
      <FormattedMessage html id="ent_acra_upload_header_content" defaultMessage="" />
      <Box mb={8}>
        <div
          data-testid={testid}
          className={`acra-upload-box ${
            dragging
              ? "acra-upload-box--dragging"
              : ""
          } ${
            isLoading
              ? "acra-upload-box--loading"
              : ""
          } ${
            isCompleted
              ? "acra-upload-box--completed"
              : ""
          }`}
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".oa"
            onChange={handleInputChange}
            className="acra-file-input"
          />

          <div className="acra-upload-content">

            {/* Upload / Completed Icon */}

            <div className="acra-upload-icon">

              <img
                src={
                  isLoading
                    ? uploadingIconUrl
                    : isAcraFileUpload
                      ? completedIconUrl
                      : uploadIconUrl
                }

                alt={
                  isAcraFileUpload
                    ? "File uploaded"
                    : "Upload file"
                }

                className="acra-upload-icon-image"
              />

            </div>


            {/* Default State */}

            {!isAcraFileUpload && (

              <>
                <div className="acra-upload-title">

                  <span className="acra-upload-drag-text">
                    Drag and drop or{" "}
                  </span>

                  <span className="acra-upload-choose-text">
                    choose files
                  </span>

                  <span className="acra-upload-drag-text">
                    {" "}to upload
                  </span>

                </div>

                <div className="acra-upload-subtitle">
                  .oa file format (max. 8MB)
                </div>
              </>

            )}


            {/* Completed State */}

            {isAcraFileUpload && (

              <>
                <div className="acra-upload-title acra-upload-title--completed">

                  {fileName ||
                    label ||
                    "Acra Upload File"}

                </div>

                <div className="acra-upload-subtitle">
                  .oa file format (max. 8MB)
                </div>
              </>

            )}


            {/* Error Modal */}

            {errorModal.show && (

              <ErrorModal
                showModal={errorModal.show}
                errorHeader={errorModal.header}
                errorMessage={errorModal.message}
                onClose={closeErrorModal}
              />

            )}


            {/* Inline Error */}

            {error && (

              <div className="acra-upload-error">
                {error}
              </div>

            )}

          </div>

        </div>

      </Box>

    </div>
  );
};


export function mapDispatchToProps(dispatch) {

  return {

    setIsAcraFileUpload: (
      isAcraFileUpload
    ) => {

      dispatch(
        setData({
          key: "isAcraFileUpload",

          data: {
            isAcraFileUpload,
          },
        })
      );

    },


    resetIsAcraFileUpload: () => {

      dispatch(
        setData({
          key: "companyDetails",

          data: {},
        })
      );

    },


    setIsDeclaredCompanyDetails: (
      isDeclaredCompanyDetails
    ) => {

      dispatch(
        setData({
          key: "isDeclaredCompanyDetails",

          data: {
            isDeclaredCompanyDetails,
          },
        })
      );

    },


    createEnterpriseWorkFlow: (
      headersValue,
      params
    ) => {

      dispatch(
        createEnterpriseWorkFlow(
          headersValue,
          params
        )
      );

    },

  };
}


const mapStateToProps =
  createStructuredSelector({

    payload:
      makeSelectOrderSummaryPayload(),

    order:
      makeSelectOrder(),

    myInfo:
      makeSelectMyInfo(),

    myInfoIsUsed:
      makeSelectMyInfoIsUsed(),

    myInfoDetails:
      makeSelectMyInfoDetails(),

    myInfoIsInvalid:
      makeSelectMyInfoIsInvalid(),

    appSettings:
      makeSelectAppSettings(),

    isAcraFileUpload:
      makeSelectAcraFileUploaded(),

    companyDetails:
      makeSelectCompanyDetails(),

  });


export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AcraUpload);